import React, { Suspense, lazy, useEffect, useRef, useState } from "react";
import {
  BrowserRouter as Router,
  Routes,
  Route,
  Navigate,
} from "react-router-dom";
import { useAuth } from "./context/AuthContext";
import { supabase } from "./services/supabaseClient";
import { Analytics } from "@vercel/analytics/react";
import { SpeedInsights } from "@vercel/speed-insights/react";
import "maplibre-gl/dist/maplibre-gl.css";

import FinanceRoute from "./components/FinanceRoute";
import MarketingRoute from "./components/MarketingRoute";
import PromoterRoute from "./components/PromoterRoute";
import AuthHashRedirect from "./components/AuthHashRedirect";
import MarketingAttributionTracker from "./components/MarketingAttributionTracker";
import AppInstallTracker from "./components/AppInstallTracker";
import RouteMemory from "./components/RouteMemory";
import GlobalServiceStatusBanner from "./components/GlobalServiceStatusBanner";
import GlobalEmergencyGate from "./components/GlobalEmergencyGate";
import RegulatoryResidenceSync from "./components/RegulatoryResidenceSync";
import Dashboard from "./pages/Dashboard";
import WorkspaceSkeleton from "./components/WorkspaceSkeleton";

// Keep the two map-based booking routes eager. Their shared location picker owns
// the MapLibre WebGL runtime. Keeping this dependency in the entry graph also
// keeps MapLibre's runtime/CSS lifecycle identical to the known-good production
// version, while every other heavyweight route remains code-split.
import ClientBooking from "./pages/ClientBooking";
import PrivateRideBooking from "./pages/PrivateRideBooking";

// Route-level code splitting keeps heavyweight Admin, maps, facial-liveness,
// calendars and marketing modules out of the initial JavaScript payload.
const VoucherPurchase = lazy(() => import("./pages/VoucherPurchase"));
const VoucherSuccess = lazy(() => import("./pages/VoucherSuccess"));
const Register = lazy(() => import("./pages/Register"));
const NewcomerRegistration = lazy(() => import("./pages/newcomer/NewcomerRegistration"));
const NewcomerDashboard = lazy(() => import("./pages/newcomer/NewcomerDashboard"));
const NewcomerBooking = lazy(() => import("./pages/newcomer/NewcomerBooking"));
const NewcomerConversion = lazy(() => import("./pages/newcomer/NewcomerConversion"));
const Login = lazy(() => import("./pages/Login"));
const Profile = lazy(() => import("./pages/Profile"));
const DriverProfile = lazy(() => import("./pages/DriverProfile"));
const DriverTools = lazy(() => import("./pages/driver-dashboard/DriverTools"));
const ForgotPassword = lazy(() => import("./pages/ForgotPassword"));
const PersonalRides = lazy(() => import("./pages/PersonalRides"));
const DriverApplication = lazy(() => import("./pages/DriverApplication"));
const AdminDashboard = lazy(() => import("./pages/admin/admin"));
const FinanceAdmin = lazy(() => import("./pages/finance/FinanceAdmin"));
const MarketingAdmin = lazy(() => import("./pages/marketing/MarketingAdmin"));
const PromoterPortal = lazy(() => import("./pages/promoter/PromoterPortal"));
const DriverSchedule = lazy(() => import("./pages/DriverSchedule"));
const LandingPage = lazy(() => import("./pages/LandingPage"));
const ServiceStatusPage = lazy(() => import("./pages/ServiceStatusPage"));
const VerifyEmail = lazy(() => import("./pages/VerifyEmail"));
const AuthCallback = lazy(() => import("./pages/AuthCallback"));
const CompleteProfile = lazy(() => import("./pages/CompleteProfile"));
const BookingSuccess = lazy(() => import("./pages/BookingSuccess"));
const RapidRideFocus = lazy(() => import("./pages/RapidRideFocus"));
const ClientBookings = lazy(() => import("./pages/ClientBookings"));
const TripShare = lazy(() => import("./pages/TripShare"));
const TermsOfService = lazy(() => import("./pages/TermsOfService"));
const DriverAgreement = lazy(() => import("./pages/DriverAgreement"));
const DriverIdentityPage = lazy(() => import("./pages/DriverIdentityPage"));
const PrivacyPolicy = lazy(() => import("./pages/PrivacyPolicy"));
const CookiePolicy = lazy(() => import("./pages/CookiePolicy"));
const AboutUs = lazy(() => import("./pages/AboutUs.jsx"));
const HowItWorks = lazy(() => import("./pages/HowItWorks"));
const HelpCenter = lazy(() => import("./pages/HelpCenter"));
const SafetyGuidelines = lazy(() => import("./pages/SafetyGuidelines"));
const ContactUs = lazy(() => import("./pages/ContactUs"));
const FAQ = lazy(() => import("./pages/FAQ"));
const ResetPassword = lazy(() => import("./pages/ResetPassword.jsx"));
const CancellationPolicy = lazy(() => import("./components/CancellationPolicy.jsx"));
const OperationsAdmin = lazy(() => import("./pages/operations/OperationsAdmin"));
const InsightSurveyPage = lazy(() => import("./pages/InsightSurveyPage"));
const EventTransportRunPage = lazy(() => import("./pages/event-transport/EventTransportRunPage"));
const EventTransportBookingPage = lazy(() => import("./pages/event-transport/EventTransportBookingPage"));
const EventTransportInvitationPage = lazy(() => import("./pages/event-transport/EventTransportInvitationPage"));
const EventTransportVerifyPage = lazy(() => import("./pages/event-transport/EventTransportVerifyPage"));
const PromoterApplicationPage = lazy(() => import("./pages/PromoterApplicationPage"));
const PromoterOnboardingPage = lazy(() => import("./pages/PromoterOnboardingPage"));

try {
  const testKey = "test_storage_check";
  localStorage.setItem(testKey, "1");
  localStorage.removeItem(testKey);
} catch (e) {
  alert(
    "⚠️ CRITICAL ERROR: Your browser is blocking Local Storage.\n\n" +
      "This app requires cookies/storage to Log In and process Payments.\n\n" +
      "PLEASE FIX:\n" +
      "1. Turn off 'Block all cookies' in settings.\n" +
      "2. If using Incognito, allow third-party cookies.\n" +
      "3. Reload the page."
  );
}

const AUTH_REDIRECT_KEYS = [
  "uniair_redirect_after_auth",
  "uniair_last_private_route",
];

const normalizePathname = (value = "") => {
  const pathname = String(value || "")
    .trim()
    .split("?")[0]
    .split("#")[0]
    .replace(/\/+$/, "");

  return pathname || "/";
};

const isPrivateRidePath = (value = "") => {
  const pathname = normalizePathname(value);

  return (
    pathname === "/private-rides" ||
    pathname.startsWith("/private-rides/")
  );
};

const clearStoredAuthRedirects = () => {
  try {
    AUTH_REDIRECT_KEYS.forEach((key) => {
      sessionStorage.removeItem(key);
      localStorage.removeItem(key);
    });
  } catch {
    // Ignore storage errors.
  }
};

function saveCurrentRouteForLogin() {
  try {
    const pathname = normalizePathname(window.location.pathname);
    const path = window.location.pathname + window.location.search;

    const ignoredPaths = [
      "/",
      "/login",
      "/register",
      "/driver-register",
      "/forgot-password",
      "/verify-email",
      "/auth/callback",
      "/reset-password",
      "/student-email",
      "/complete-profile",
    ];

    // Private Rides must never become an automatic post-login destination.
    // Access is checked live against the admin-controlled market setting.
    if (ignoredPaths.includes(pathname) || isPrivateRidePath(pathname)) {
      clearStoredAuthRedirects();
      return;
    }

    sessionStorage.setItem("uniair_redirect_after_auth", path);
    sessionStorage.removeItem("uniair_last_private_route");
    localStorage.removeItem("uniair_last_private_route");
  } catch {
    // Ignore storage errors.
  }
}

const resolveStaffWorkspace = (adminAccess) => {
  const roles = (adminAccess?.roles || []).map((role) =>
    String(role).trim().toLowerCase()
  );

  if (roles.some((role) => ["superadmin", "admin"].includes(role))) return "/admin";
  if (roles.includes("operations")) return "/operations-admin";
  if (roles.includes("finance")) return "/finance-admin";
  if (roles.some((role) => ["marketing_lead", "marketing", "marketing_viewer"].includes(role))) {
    return "/marketing-admin";
  }
  if (roles.includes("promoter") || adminAccess?.is_promoter === true) return "/promoter";
  return null;
};

function FullPageLoader() {
  return <WorkspaceSkeleton label="Loading UniAir" />;
}

function ProfileRecovery() {
  const { profileLoading, profileError, retryProfile } = useAuth();

  return (
    <div className="min-h-screen flex items-center justify-center bg-gray-50 px-4">
      <div className="w-full max-w-sm rounded-2xl bg-white p-6 text-center shadow-md">
        <h2 className="text-lg font-bold text-gray-900">
          Reconnecting to UniAir
        </h2>
        <p className="mt-2 text-sm leading-6 text-gray-600">
          You are still signed in, but we could not load your profile just now.
          Check your connection and try again.
        </p>
        {profileError?.message ? (
          <p className="mt-3 rounded-xl bg-gray-50 px-3 py-2 text-xs text-gray-500">
            {profileError.message}
          </p>
        ) : null}
        <button
          type="button"
          onClick={() => retryProfile()}
          disabled={profileLoading}
          className="mt-5 w-full rounded-xl bg-[#720e1e] px-4 py-3 text-sm font-bold text-white disabled:opacity-60"
        >
          {profileLoading ? "Reconnecting..." : "Try again"}
        </button>
      </div>
    </div>
  );
}

function ProtectedRoute({ children }) {
  const { user, isAuthenticated, loading, authReady } = useAuth();
  const hasGrantedAccessRef = useRef(false);

  if (authReady && isAuthenticated && user) {
    hasGrantedAccessRef.current = true;
  }

  // Only use the full-page loader during the first authentication boot.
  // Supabase may briefly refresh the session when a hidden browser tab becomes
  // visible. Unmounting the route at that point destroys forms, dialogs and
  // scroll position, so keep an already-authorised workspace mounted.
  if (loading || !authReady) {
    return hasGrantedAccessRef.current ? children : <FullPageLoader />;
  }

  if (!isAuthenticated) {
    hasGrantedAccessRef.current = false;
    saveCurrentRouteForLogin();
    return <Navigate to="/login" replace />;
  }

  if (!user) {
    return hasGrantedAccessRef.current ? children : <ProfileRecovery />;
  }

  return children;
}

function PersistentAdminRoute({ children }) {
  const {
    user,
    isAuthenticated,
    loading,
    authReady,
    adminAccess,
    adminAccessLoading,
    isSuperAdmin,
  } = useAuth();

  // Once this browser session has passed the Admin access check, never replace
  // the mounted workspace merely because Supabase is refreshing the token or
  // re-checking the role RPC. Replacing it destroys open dialogs, typed notes,
  // selected filters, scroll position and the active tab's local state.
  const hasGrantedAdminAccessRef = useRef(false);

  const roles = (adminAccess?.roles || []).map((role) =>
    String(role || "").trim().toLowerCase()
  );

  const canAccessFullAdmin =
    roles.some((role) => ["admin", "superadmin"].includes(role)) ||
    isSuperAdmin === true;

  if (user && canAccessFullAdmin) {
    hasGrantedAdminAccessRef.current = true;
  }

  const accessCheckPending = loading || !authReady || adminAccessLoading;

  if (accessCheckPending) {
    return hasGrantedAdminAccessRef.current ? children : <FullPageLoader />;
  }

  if (!isAuthenticated || !user) {
    hasGrantedAdminAccessRef.current = false;
    saveCurrentRouteForLogin();
    return <Navigate to="/login" replace />;
  }

  if (canAccessFullAdmin) {
    hasGrantedAdminAccessRef.current = true;
    return children;
  }

  // A failed background role refresh can temporarily return null. Backend RLS
  // still protects every admin query, so preserve the already-open interface
  // rather than destroying the user's work. A resolved non-admin role object
  // below still redirects immediately.
  if (hasGrantedAdminAccessRef.current && adminAccess == null) {
    return children;
  }

  hasGrantedAdminAccessRef.current = false;
  const workspace = resolveStaffWorkspace(adminAccess);
  return <Navigate to={workspace || "/dashboard"} replace />;
}

function OperationsRoute({ children }) {
  const {
    user,
    isAuthenticated,
    loading,
    authReady,
    adminAccess,
    adminAccessLoading,
  } = useAuth();
  const hasGrantedOperationsAccessRef = useRef(false);

  const roles = (adminAccess?.roles || []).map((role) =>
    String(role).trim().toLowerCase()
  );
  const canAccessOperations = roles.some((role) =>
    ["operations", "admin", "superadmin"].includes(role)
  );

  if (user && canAccessOperations) {
    hasGrantedOperationsAccessRef.current = true;
  }

  const accessCheckPending = loading || !authReady || adminAccessLoading;

  // Preserve the mounted Operations workspace during background session and
  // role checks. This prevents open filters, forms and local state from being
  // destroyed whenever the browser tab regains focus.
  if (accessCheckPending) {
    return hasGrantedOperationsAccessRef.current
      ? children
      : <FullPageLoader />;
  }

  if (!isAuthenticated || !user) {
    hasGrantedOperationsAccessRef.current = false;
    saveCurrentRouteForLogin();
    return <Navigate to="/login" replace />;
  }

  if (canAccessOperations) {
    hasGrantedOperationsAccessRef.current = true;
    return children;
  }

  if (hasGrantedOperationsAccessRef.current && adminAccess == null) {
    return children;
  }

  hasGrantedOperationsAccessRef.current = false;
  const workspace = resolveStaffWorkspace(adminAccess);
  return <Navigate to={workspace || "/dashboard"} replace />;
}

function BookingRoute({ children }) {
  const {
    user,
    isAuthenticated,
    loading,
    authReady,
    verificationAccessLoading,
    canBookTrips,
    adminAccess,
  } = useAuth();

  if (loading || !authReady || verificationAccessLoading) {
    return <FullPageLoader />;
  }

  if (!isAuthenticated) {
    saveCurrentRouteForLogin();
    return <Navigate to="/login" replace />;
  }

  if (!user) return <ProfileRecovery />;

  const staffWorkspace = resolveStaffWorkspace(adminAccess);
  if (staffWorkspace) return <Navigate to={staffWorkspace} replace />;
  if (!canBookTrips) return <Navigate to="/dashboard" replace />;

  return children;
}

function PrivateRideRoute({ children }) {
  const { user, isAuthenticated, loading, authReady, verificationAccessLoading, canBookTrips } = useAuth();
  const [capabilityLoading, setCapabilityLoading] = useState(true);
  const [canRequestPrivateRide, setCanRequestPrivateRide] = useState(false);

  useEffect(() => {
    let cancelled = false;

    const checkCapability = async () => {
      if (loading || !authReady) return;

      if (!user?.id) {
        if (!cancelled) {
          setCanRequestPrivateRide(false);
          setCapabilityLoading(false);
        }
        return;
      }

      try {
        setCapabilityLoading(true);

        const { data, error } = await supabase.rpc(
          "get_my_private_ride_capability_v1"
        );

        if (error) throw error;

        if (!cancelled) {
          setCanRequestPrivateRide(data?.can_request === true);
        }
      } catch (error) {
        console.error("Private Ride route capability check failed:", error);

        // Fail closed. A temporary capability-check error must never expose
        // a disabled or unavailable passenger booking page.
        if (!cancelled) {
          setCanRequestPrivateRide(false);
        }
      } finally {
        if (!cancelled) {
          setCapabilityLoading(false);
        }
      }
    };

    checkCapability();

    return () => {
      cancelled = true;
    };
  }, [user?.id, user?.market_id, loading, authReady]);

  if (loading || !authReady || verificationAccessLoading || capabilityLoading) {
    return <FullPageLoader />;
  }

  if (!isAuthenticated) {
    clearStoredAuthRedirects();
    return <Navigate to="/login" replace />;
  }

  if (!user) return <ProfileRecovery />;

  if (!canBookTrips) {
    clearStoredAuthRedirects();
    return <Navigate to="/profile" replace />;
  }

  if (!canRequestPrivateRide) {
    clearStoredAuthRedirects();
    return <Navigate to="/dashboard" replace />;
  }

  return children;
}

function DriverAvailabilityRoute({ children }) {
  const {
    user,
    isAuthenticated,
    loading,
    authReady,
    verificationAccessLoading,
    isDriver,
    adminAccess,
    canSetDriverAvailability,
  } = useAuth();

  if (loading || !authReady || verificationAccessLoading) {
    return <FullPageLoader />;
  }

  if (!isAuthenticated) {
    saveCurrentRouteForLogin();
    return <Navigate to="/login" replace />;
  }

  if (!user) return <ProfileRecovery />;

  const staffWorkspace = resolveStaffWorkspace(adminAccess);
  if (staffWorkspace) return <Navigate to={staffWorkspace} replace />;
  if (!isDriver) return <Navigate to="/dashboard" replace />;
  if (!canSetDriverAvailability) return <Navigate to="/dashboard" replace />;

  return children;
}

function DriverProfileRoute({ children }) {
  const {
    user,
    isAuthenticated,
    loading,
    authReady,
    verificationAccessLoading,
    isDriver,
    adminAccess,
  } = useAuth();

  const hasGrantedDriverProfileAccessRef = useRef(false);

  // Once this route has already confirmed the signed-in user is a driver,
  // preserve the mounted page during background verification-state refreshes.
  //
  // This is important for facial verification because completing a check
  // refreshes get_my_verification_access_state(). We must not unmount the
  // identity page while that background refresh is taking place.
  if (authReady && isAuthenticated && user && isDriver) {
    hasGrantedDriverProfileAccessRef.current = true;
  }

  const accessCheckPending =
    loading || !authReady || verificationAccessLoading;

  if (accessCheckPending) {
    return hasGrantedDriverProfileAccessRef.current
      ? children
      : <FullPageLoader />;
  }

  if (!isAuthenticated) {
    hasGrantedDriverProfileAccessRef.current = false;
    saveCurrentRouteForLogin();
    return <Navigate to="/login" replace />;
  }

  if (!user) {
    return <ProfileRecovery />;
  }

  const staffWorkspace = resolveStaffWorkspace(adminAccess);

  if (staffWorkspace) {
    hasGrantedDriverProfileAccessRef.current = false;
    return <Navigate to={staffWorkspace} replace />;
  }

  if (!isDriver) {
    hasGrantedDriverProfileAccessRef.current = false;
    return <Navigate to="/dashboard" replace />;
  }

  hasGrantedDriverProfileAccessRef.current = true;

  return children;
}

function DashboardEntry() {
  const {
    user,
    authUser,
    isAuthenticated,
    loading,
    authReady,
    verificationAccessLoading,
    adminAccessLoading,
    isAdmin,
    isSuperAdmin,
    isFinanceAdmin,
    adminAccess,
  } = useAuth();

  if (loading || !authReady || verificationAccessLoading || adminAccessLoading) {
    return <FullPageLoader />;
  }

  if (!isAuthenticated) {
    saveCurrentRouteForLogin();
    return <Navigate to="/login" replace />;
  }

  if (!user) return <ProfileRecovery />;

  // Use the backend role list. Marketing and promoter workspaces are separate
  // from full Admin, Operations and Finance access.
  const staffWorkspace = resolveStaffWorkspace(adminAccess);
  if (staffWorkspace) return <Navigate to={staffWorkspace} replace />;

  // Backward-compatible fallback while legacy staff roles are loading.
  if (isSuperAdmin || isAdmin) return <Navigate to="/admin" replace />;
  if (isFinanceAdmin) return <Navigate to="/finance-admin" replace />;

  // Newcomer accounts use UniAir's passenger design, with separately enforced
  // per-vehicle pricing. A verified conversion uses the normal dashboard.
  const metadataPath = String(authUser?.user_metadata?.registration_path || '');
  const hasPendingNewcomerSignup =
    (metadataPath === 'incoming_student' || metadataPath === 'newcomer_guardian') &&
    !user?.newcomer_student_profile_id;
  if (
    user?.access_tier === 'newcomer' ||
    user?.access_tier === 'newcomer_guardian' ||
    hasPendingNewcomerSignup
  ) return <NewcomerDashboard />;

  if (user?.needsProfile) return <Navigate to="/complete-profile" replace />;

  return <Dashboard />;
}

function App() {
  useEffect(() => {
    if (!("serviceWorker" in navigator)) return undefined;

    const handleServiceWorkerMessage = (event) => {
      if (event?.data?.type !== "UNI_AIR_NOTIFICATION_CLICK") return;

      try {
        const target = new URL(
          event.data.url || "/dashboard",
          window.location.origin
        );

        if (target.origin !== window.location.origin) return;

        window.location.assign(
          `${target.pathname}${target.search}${target.hash}`
        );
      } catch (error) {
        console.error("Invalid notification destination:", error);
      }
    };

    navigator.serviceWorker.addEventListener(
      "message",
      handleServiceWorkerMessage
    );

    return () => {
      navigator.serviceWorker.removeEventListener(
        "message",
        handleServiceWorkerMessage
      );
    };
  }, []);

  return (
    <Router>
      <GlobalServiceStatusBanner />
      <GlobalEmergencyGate />
      <RegulatoryResidenceSync />
      <AuthHashRedirect />
      <MarketingAttributionTracker />
      <AppInstallTracker />
      <RouteMemory />

      <Suspense fallback={<WorkspaceSkeleton label="Opening UniAir page" />}>
      <Routes>
        {/* PUBLIC */}
        <Route path="/" element={<LandingPage />} />
        <Route path="/status" element={<ServiceStatusPage />} />
        <Route path="/register" element={<Register />} />
        <Route path="/newcomer" element={<NewcomerRegistration />} />
        <Route path="/driver-register" element={<Register />} />
        <Route path="/login" element={<Login />} />
        <Route path="/forgot-password" element={<ForgotPassword />} />
        <Route path="/verify-email" element={<VerifyEmail />} />
        <Route path="/auth/callback" element={<AuthCallback />} />
        <Route path="/about" element={<AboutUs />} />
        <Route path="/how-it-works" element={<HowItWorks />} />
        <Route path="/help" element={<HelpCenter />} />
        <Route path="/safety" element={<SafetyGuidelines />} />
        <Route path="/contact" element={<ContactUs />} />
        <Route path="/faq" element={<FAQ />} />
        <Route path="/reset-password" element={<ResetPassword />} />
        <Route path="/vouchers" element={<VoucherPurchase />} />
        <Route path="/vouchers/success" element={<VoucherSuccess />} />
        <Route path="/trip-share/:token" element={<TripShare />} />
        <Route path="/event-transport/verify" element={<EventTransportVerifyPage />} />
        <Route path="/apply/promoter/:slug" element={<PromoterApplicationPage />} />
        <Route path="/promoter-onboarding/:token" element={<PromoterOnboardingPage />} />
        <Route path="/insight-survey/:token" element={<InsightSurveyPage />}
/>

        {/* LEGAL */}
        <Route path="/terms" element={<TermsOfService />} />
        <Route path="/driver-terms" element={<DriverAgreement />} />
        <Route path="/privacy" element={<PrivacyPolicy />} />
        <Route path="/cookies" element={<CookiePolicy />} />
        <Route path="/cancellation" element={<CancellationPolicy />} />

        {/* PROTECTED */}

        <Route
          path="/operations-admin"
          element={
            <OperationsRoute>
              <OperationsAdmin />
            </OperationsRoute>
          }
        />

        {/* Legacy URL only: the old hard-coded Stellenbosch student-email gate is retired. */}
        <Route path="/student-email" element={<Navigate to="/dashboard" replace />} />

        <Route
          path="/complete-profile"
          element={
            <ProtectedRoute>
              <CompleteProfile />
            </ProtectedRoute>
          }
        />

        <Route path="/dashboard" element={<DashboardEntry />} />
        <Route path="/ride-now/trip/:tripId"
          element={<ProtectedRoute><RapidRideFocus /></ProtectedRoute>} />
        <Route
          path="/newcomer/book"
          element={<ProtectedRoute><NewcomerBooking /></ProtectedRoute>}
        />
        <Route
          path="/newcomer/convert"
          element={<ProtectedRoute><NewcomerConversion /></ProtectedRoute>}
        />
        <Route path="/driver-dashboard" element={<DashboardEntry />} />

        <Route
          path="/personal-rides"
          element={
            <ProtectedRoute>
              <PersonalRides />
            </ProtectedRoute>
          }
        />

        <Route
          path="/profile"
          element={
            <ProtectedRoute>
              <Profile />
            </ProtectedRoute>
          }
        />

        <Route
          path="/driver-profile"
          element={
            <DriverProfileRoute>
              <DriverProfile />
            </DriverProfileRoute>
          }
        />

        <Route
          path="/driver-tools/:tool"
          element={
            <DriverProfileRoute>
              <DriverTools />
            </DriverProfileRoute>
          }
        />

        <Route
          path="/driver-identity"
          element={
            <DriverProfileRoute>
              <DriverIdentityPage />
            </DriverProfileRoute>
          }
        />

        <Route
          path="/driver-application"
          element={
            <ProtectedRoute>
              <DriverApplication />
            </ProtectedRoute>
          }
        />

        <Route
          path="/schedule"
          element={
            <DriverAvailabilityRoute>
              <DriverSchedule />
            </DriverAvailabilityRoute>
          }
        />


        <Route
          path="/event-transport/runs/:runId"
          element={
            <BookingRoute>
              <EventTransportRunPage />
            </BookingRoute>
          }
        />

        <Route
          path="/event-transport/bookings/:bookingId"
          element={
            <BookingRoute>
              <EventTransportBookingPage />
            </BookingRoute>
          }
        />

        <Route
          path="/event-transport/invitations/:passengerId"
          element={
            <ProtectedRoute>
              <EventTransportInvitationPage />
            </ProtectedRoute>
          }
        />

        <Route
          path="/private-rides/*"
          element={
            <PrivateRideRoute>
              <PrivateRideBooking />
            </PrivateRideRoute>
          }
        />

        <Route
          path="/book"
          element={
            <BookingRoute>
              <ClientBooking />
            </BookingRoute>
          }
        />

        <Route
          path="/booking-success"
          element={
            <ProtectedRoute>
              <BookingSuccess />
            </ProtectedRoute>
          }
        />

        <Route
          path="/my-bookings"
          element={
            <ProtectedRoute>
              <ClientBookings />
            </ProtectedRoute>
          }
        />

        <Route
          path="/marketing-admin"
          element={
            <ProtectedRoute>
              <MarketingRoute>
                <MarketingAdmin />
              </MarketingRoute>
            </ProtectedRoute>
          }
        />

        <Route
          path="/promoter"
          element={
            <ProtectedRoute>
              <PromoterRoute>
                <PromoterPortal />
              </PromoterRoute>
            </ProtectedRoute>
          }
        />

        <Route
          path="/admin"
          element={
            <ProtectedRoute>
              <PersistentAdminRoute>
                <AdminDashboard />
              </PersistentAdminRoute>
            </ProtectedRoute>
          }
        />

        <Route
          path="/finance-admin"
          element={
            <ProtectedRoute>
              <FinanceRoute>
                <FinanceAdmin />
              </FinanceRoute>
            </ProtectedRoute>
          }
        />

        {/* FALLBACK */}
        <Route path="*" element={<Navigate to="/dashboard" replace />} />
      </Routes>
      </Suspense>

      <Analytics />
      <SpeedInsights />
    </Router>
  );
}

export default App;